import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, TableProperties } from 'lucide-react';
import { CTPAnsweredQuestion } from '../../types/compareThePair';
import { CTPSummaryModal } from './CTPSummaryModal';
import { get } from '../../utils/api';

interface VoteCounts {
  votesA: number;
  votesB: number;
  totalVotes: number;
}

interface CTPVoteSlideProps {
  nameA: string;
  nameB: string;
  symbolA: string;
  symbolB: string;
  logoUrlA: string | null;
  logoUrlB: string | null;
  gameDate: string;
  savedVote: 'A' | 'B' | null;
  onVoteSaved: (vote: 'A' | 'B') => void;
  onComplete: () => void;
  onPrev: () => void;
  answeredQuestions: CTPAnsweredQuestion[];
}

const CompanyLogo: React.FC<{ logoUrl: string | null; name: string; symbol: string }> = ({
  logoUrl,
  name,
  symbol,
}) => {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name}
        className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-contain bg-white p-2"
      />
    );
  }
  return (
    <div
      className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center text-xl font-bold bg-muted text-foreground"
    >
      {symbol.slice(0, 2)}
    </div>
  );
};

export const CTPVoteSlide: React.FC<CTPVoteSlideProps> = ({
  nameA,
  nameB,
  symbolA,
  symbolB,
  logoUrlA,
  logoUrlB,
  gameDate,
  savedVote,
  onVoteSaved,
  onComplete,
  onPrev,
  answeredQuestions,
}) => {
  const [showSummary, setShowSummary] = useState(false);
  const [voteCounts, setVoteCounts] = useState<VoteCounts | null>(null);
  const [animateBars, setAnimateBars] = useState(false);

  const hasVoted = savedVote !== null;

  // Fetch vote counts after voting
  useEffect(() => {
    if (!hasVoted) return;
    let cancelled = false;

    async function fetchVotes() {
      try {
        const res = await get<VoteCounts>(`/api/compare-the-pair/votes/${gameDate}`);
        if (!cancelled) {
          setVoteCounts(res.data);
          // Trigger bar animation after a brief delay
          requestAnimationFrame(() => {
            setTimeout(() => setAnimateBars(true), 50);
          });
        }
      } catch {
        // Silently fail — vote results are non-critical
      }
    }

    fetchVotes();
    return () => { cancelled = true; };
  }, [hasVoted, gameDate]);

  const pctA = voteCounts && voteCounts.totalVotes > 0
    ? Math.round((voteCounts.votesA / voteCounts.totalVotes) * 100)
    : 50;
  const pctB = voteCounts && voteCounts.totalVotes > 0
    ? Math.round((voteCounts.votesB / voteCounts.totalVotes) * 100)
    : 50;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Heading */}
      <div className="flex flex-col items-center mb-3 sm:mb-4 animate-fade-up">
        <p
          className="text-xs font-medium uppercase tracking-widest mb-1"
          style={{ color: 'var(--ctp-accent)' }}
        >
          Final Question
        </p>
        <h2
          className="text-xl sm:text-2xl font-serif font-bold px-4 text-center"
          style={{ color: 'hsl(var(--foreground))' }}
        >
          Which company would you rather own?
        </h2>
      </div>

      {/* Show Result Summary button */}
      {answeredQuestions.length > 0 && (
        <div className="flex justify-center mb-2 animate-fade-up">
          <button
            onClick={() => setShowSummary(true)}
            className="flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition-all hover:opacity-90 active:scale-[0.97]"
            style={{
              backgroundColor: 'hsl(var(--muted))',
              color: 'hsl(var(--muted-foreground))',
              border: '1px solid hsl(var(--border))',
            }}
          >
            <TableProperties className="w-3.5 h-3.5" />
            Show Metric Summary
          </button>
        </div>
      )}

      {/* Two-panel grid */}
      <div className="flex-1 relative grid grid-cols-2 gap-2 sm:gap-3 min-h-0 animate-fade-up">
        {/* Panel A */}
        <button
          onClick={() => !hasVoted && onVoteSaved('A')}
          disabled={hasVoted}
          className={`relative rounded-2xl flex flex-col items-center justify-center p-3 sm:p-4 overflow-hidden transition-all ${
            !hasVoted
              ? 'cursor-pointer hover:scale-[1.01] active:scale-[0.98]'
              : 'cursor-default'
          }`}
          style={{
            background: 'linear-gradient(135deg, rgba(244, 63, 94, 0.12), rgba(249, 115, 22, 0.12))',
            border: `2px solid ${
              hasVoted && savedVote === 'A'
                ? 'var(--ctp-accent)'
                : 'var(--ctp-card-border)'
            }`,
          }}
        >
          {/* Vote result bar */}
          {hasVoted && voteCounts && (
            <div
              className="absolute bottom-0 left-0 right-0 transition-all duration-700 ease-out rounded-b-2xl"
              style={{
                height: animateBars ? `${pctA}%` : '0%',
                background: savedVote === 'A'
                  ? 'rgba(59, 130, 246, 0.25)'
                  : 'rgba(59, 130, 246, 0.12)',
              }}
            />
          )}
          <div className="flex flex-col items-center z-10">
            <CompanyLogo logoUrl={logoUrlA} name={nameA} symbol={symbolA} />
            <p className="font-semibold text-sm sm:text-base mt-2 text-center leading-tight" style={{ color: 'hsl(var(--foreground))' }}>
              {nameA}
            </p>
            <p className="text-xs font-mono mt-0.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
              {symbolA}
            </p>
            {/* Vote result overlay */}
            {hasVoted && voteCounts && (
              <div className="mt-2 flex flex-col items-center z-10">
                <p className="text-2xl sm:text-3xl font-bold" style={{ color: 'var(--ctp-accent)' }}>
                  {pctA}%
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
                  {voteCounts.votesA.toLocaleString()} {voteCounts.votesA === 1 ? 'vote' : 'votes'}
                </p>
              </div>
            )}
          </div>
        </button>

        {/* Panel B */}
        <button
          onClick={() => !hasVoted && onVoteSaved('B')}
          disabled={hasVoted}
          className={`relative rounded-2xl flex flex-col items-center justify-center p-3 sm:p-4 overflow-hidden transition-all ${
            !hasVoted
              ? 'cursor-pointer hover:scale-[1.01] active:scale-[0.98]'
              : 'cursor-default'
          }`}
          style={{
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.12), rgba(6, 182, 212, 0.12))',
            border: `2px solid ${
              hasVoted && savedVote === 'B'
                ? 'var(--ctp-accent)'
                : 'var(--ctp-card-border)'
            }`,
          }}
        >
          {/* Vote result bar */}
          {hasVoted && voteCounts && (
            <div
              className="absolute bottom-0 left-0 right-0 transition-all duration-700 ease-out rounded-b-2xl"
              style={{
                height: animateBars ? `${pctB}%` : '0%',
                background: savedVote === 'B'
                  ? 'rgba(59, 130, 246, 0.25)'
                  : 'rgba(59, 130, 246, 0.12)',
              }}
            />
          )}
          <div className="flex flex-col items-center z-10">
            <CompanyLogo logoUrl={logoUrlB} name={nameB} symbol={symbolB} />
            <p className="font-semibold text-sm sm:text-base mt-2 text-center leading-tight" style={{ color: 'hsl(var(--foreground))' }}>
              {nameB}
            </p>
            <p className="text-xs font-mono mt-0.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
              {symbolB}
            </p>
            {/* Vote result overlay */}
            {hasVoted && voteCounts && (
              <div className="mt-2 flex flex-col items-center z-10">
                <p className="text-2xl sm:text-3xl font-bold" style={{ color: 'var(--ctp-accent)' }}>
                  {pctB}%
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
                  {voteCounts.votesB.toLocaleString()} {voteCounts.votesB === 1 ? 'vote' : 'votes'}
                </p>
              </div>
            )}
          </div>
        </button>
      </div>

      {/* Bottom bar */}
      <div className="flex items-center pt-3 pb-1">
        {/* Back button */}
        <div className="w-10">
          <button
            onClick={onPrev}
            className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:opacity-80 active:scale-[0.95]"
            style={{ backgroundColor: 'hsl(var(--muted))' }}
          >
            <ChevronLeft className="w-4 h-4" style={{ color: 'hsl(var(--muted-foreground))' }} />
          </button>
        </div>

        {/* Skip / Next — centered */}
        <div className="flex-1 flex justify-center">
          <button
            onClick={onComplete}
            className="flex items-center gap-2 px-8 py-2.5 rounded-full font-semibold text-sm transition-all hover:opacity-90 active:scale-[0.97]"
            style={{ backgroundColor: 'var(--ctp-accent)', color: '#fff' }}
          >
            {hasVoted ? 'Next' : 'Skip'}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Spacer for balance */}
        <div className="w-10" />
      </div>

      {/* Summary Modal */}
      <CTPSummaryModal
        isOpen={showSummary}
        onClose={() => setShowSummary(false)}
        answeredQuestions={answeredQuestions}
        nameA={nameA}
        nameB={nameB}
        symbolA={symbolA}
        symbolB={symbolB}
        logoUrlA={logoUrlA}
        logoUrlB={logoUrlB}
      />
    </div>
  );
};
